import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const services = [
  {
    icon: 'ri-cake-3-line',
    title: 'Custom Orders',
    description: 'Every creation is made to match your unique vision — from concept to the final, delicious bite.',
    items: ['Custom Cakes', 'Decorated Cookies', 'Artisan Pastries', 'Pies & Tarts'],
    color: 'bg-blush-100',
    iconColor: 'text-blush-500',
    image: 'https://public.readdy.ai/ai/img_res/83c20001877d164c6d1192c339920f7a.jpg',
  },
  {
    icon: 'ri-star-line',
    title: 'Bakery Favorites',
    description: 'Our beloved signature treats that keep Lubbock customers coming back for more every season.',
    items: ['Cake Pops', 'Seasonal Mini Desserts', 'Neiman Marcus Cake', 'Cheesecakes', 'Mini Pies — Key Lime & Millionaire'],
    color: 'bg-gold-300/30',
    iconColor: 'text-gold-600',
    image: 'https://readdy.ai/api/search-image?query=assorted%20beautiful%20bakery%20treats%20display%20with%20cake%20pops%20cheesecake%20mini%20pies%20cookies%20arranged%20on%20elegant%20cream%20marble%20surface%2C%20warm%20golden%20lighting%2C%20professional%20food%20photography%2C%20tempting%20dessert%20spread%2C%20bakery%20showcase%2C%20Lubbock%20Texas%20small%20business&width=400&height=300&seq=svc002&orientation=landscape',
  },
  {
    icon: 'ri-graduation-cap-line',
    title: 'Classes & Supplies',
    description: 'Learn the art of cake decorating with our hands-on classes and find the supplies you need to create at home.',
    items: ['Cake Decorating Classes', 'Seasonal Workshops', 'Baking Supplies', 'Decorating Tools & Kits'],
    color: 'bg-warm-200',
    iconColor: 'text-charcoal-700',
    image: 'https://readdy.ai/api/search-image?query=cake%20decorating%20class%20workshop%20scene%20with%20students%20learning%20to%20pipe%20frosting%20on%20cakes%2C%20blush%20pink%20aprons%2C%20warm%20cozy%20kitchen%20setting%2C%20hands%20on%20baking%20tutorial%2C%20professional%20bakery%20education%2C%20warm%20lighting%2C%20friendly%20and%20inviting%20atmosphere&width=400&height=300&seq=svc003&orientation=landscape',
  },
];

export default function ServicesSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const cards = ref.current?.querySelectorAll('.service-card');
          cards?.forEach((card, i) => {
            setTimeout(() => {
              card.classList.add('opacity-100', 'translate-y-0');
              card.classList.remove('opacity-0', 'translate-y-8');
            }, i * 150);
          });
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="services" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-px w-10 bg-gold-500" />
            <span className="text-gold-500 text-xs uppercase tracking-[0.25em] font-sans font-semibold">What We Create</span>
            <div className="h-px w-10 bg-gold-500" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-charcoal-800 mb-4">
            Something Sweet for<br /><em className="italic text-blush-500">Every Occasion</em>
          </h2>
          <p className="text-warm-gray text-base md:text-lg max-w-2xl mx-auto font-sans font-light leading-relaxed">
            From elegant celebrations to everyday indulgences, every treat is handcrafted with attention to detail and a whole lot of love.
          </p>
        </div>

        {/* Cards */}
        <div ref={ref} className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {services.map((svc) => (
            <div
              key={svc.title}
              className="service-card opacity-0 translate-y-8 transition-all duration-700 ease-out rounded-2xl overflow-hidden border border-warm-200 hover:scale-[1.02] hover:border-blush-200 transition-transform"
            >
              {/* Image */}
              <div className="w-full h-48 overflow-hidden">
                <img
                  src={svc.image}
                  alt={svc.title}
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                />
              </div>
              {/* Content */}
              <div className={`${svc.color} p-8 flex flex-col gap-4 h-full`}>
                <div className={`w-12 h-12 flex items-center justify-center rounded-full bg-white ${svc.iconColor}`}>
                  <i className={`${svc.icon} text-xl`} />
                </div>
                <h3 className="font-serif text-2xl text-charcoal-800">{svc.title}</h3>
                <p className="text-warm-gray text-sm leading-relaxed font-sans">{svc.description}</p>
                <ul className="flex flex-col gap-2 mt-2">
                  {svc.items.map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-charcoal-700 font-sans">
                      <span className="w-1.5 h-1.5 rounded-full bg-blush-400 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <Link
            to="/custom-orders"
            className="inline-flex items-center gap-2 bg-charcoal-800 hover:bg-charcoal-900 text-white px-8 py-4 rounded-full font-sans font-semibold text-sm tracking-wide whitespace-nowrap transition-all duration-200 hover:scale-105 cursor-pointer"
          >
            Start Your Custom Order
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
}
